import { useCallback, useEffect, useRef, useState } from "react";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { LanguageProvider } from "@/i18n/LanguageContext";
import { SplashOverlay } from "@/components/lexaegis/SplashOverlay";
import { Header } from "@/components/lexaegis/Header";
import { Hero } from "@/components/lexaegis/Hero";
import { Marquee } from "@/components/lexaegis/Marquee";
import { Framework } from "@/components/lexaegis/Framework";
import { Advantage } from "@/components/lexaegis/Advantage";
import { Workflow } from "@/components/lexaegis/Workflow";
import { Panel } from "@/components/lexaegis/Panel";
import { Pricing } from "@/components/lexaegis/Pricing";
import { PressSection } from "@/components/lexaegis/PressSection";
import { IntakeModal } from "@/components/lexaegis/IntakeModal";
import { PortalModal } from "@/components/lexaegis/PortalModal";
import { Footer } from "@/components/lexaegis/Footer";
import "@/App.css";

function App() {
  const [splashOpen, setSplashOpen] = useState(true);
  const [intakeOpen, setIntakeOpen] = useState(false);
  const [portalOpen, setPortalOpen] = useState(false);
  const lenisRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    lenisRef.current = lenis;
    let raf;
    const loop = (time) => { lenis.raf(time); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);

    const onClick = (e) => {
      const a = e.target.closest?.('a[href^="#"]');
      if (!a) return;
      const target = document.querySelector(a.getAttribute("href"));
      if (target) {
        e.preventDefault();
        lenis.scrollTo(target, { offset: -72, duration: 1.4 });
      }
    };
    document.addEventListener("click", onClick);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("click", onClick);
      lenis.destroy();
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = splashOpen ? "hidden" : "";
    if (splashOpen) lenisRef.current?.stop();
    else lenisRef.current?.start();
    return () => { document.body.style.overflow = ""; };
  }, [splashOpen]);

  const enterSite = useCallback(() => setSplashOpen(false), []);
  const openIntake = useCallback(() => setIntakeOpen(true), []);
  const openPortal = useCallback(() => setPortalOpen(true), []);

  return (
    <LanguageProvider>
      <div className="min-h-screen bg-navy text-slate-200">
        <div className="grain-overlay" />
        <SplashOverlay open={splashOpen} onEnter={enterSite} />
        <Header onInitiate={openIntake} onPortal={openPortal} />
        <main>
          <Hero onInitiate={openIntake} started={!splashOpen} />
          <Marquee />
          <Framework />
          <Advantage />
          <Workflow />
          <Panel />
          <Pricing onInitiate={openIntake} />
          <PressSection />
        </main>
        <Footer onPortal={openPortal} />
        <IntakeModal open={intakeOpen} onClose={() => setIntakeOpen(false)} />
        <PortalModal open={portalOpen} onClose={() => setPortalOpen(false)} />
        <Toaster theme="dark" position="top-center" />
      </div>
    </LanguageProvider>
  );
}

export default App;
